import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { RadioButton } from "../RadioButton/RadioButton";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import {
	convertArbitraryValueForTrackingId,
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { ConditionalWrapper } from "../ConditionalWrapper/ConditionalWrapper";
import { InputWithMoreInfo } from "../InputWithMoreInfo/InputWithMoreInfo";

export type RadioButtonGroupItem<TValue> = {
	text: string;
	value: TValue;
	isDisabled?: boolean;
	valueTrackingId?: string;
	onMoreInfo?: () => void;
};

export type RadioButtonGroupProps<TValue> = {
	value: TValue | undefined;
	onChange(value: TValue): void;
	options: readonly RadioButtonGroupItem<TValue>[];
	ellipseText?: boolean;
	trackingId?: string;
	isDisabled?: boolean;
};

export const RadioButtonGroup = <TValue,>(
	props: RadioButtonGroupProps<TValue>
): ReactElement => {
	const {
		value,
		onChange,
		options,
		ellipseText,
		trackingId,
		isDisabled = false
	} = props;

	const [groupName] = useState<Guid>(() => newGuid());

	const changeHandler = (isEnabled: boolean, value: TValue): void => {
		if (!isEnabled) {
			return;
		}
		onChange(value);
	};
	const getValueTrackingId = useCallback(
		(item: RadioButtonGroupItem<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	return (
		<>
			{options.map((item, index) => {
				const itemTrackingId: string | undefined = generateTrackingId(trackingId, `|${getValueTrackingId(item)}`);
				return (
					<ConditionalWrapper
						key={index}
						condition={isNotNil(item.onMoreInfo)}
						wrapper={(x) => (
							<InputWithMoreInfo
								onMoreInfo={ensureNotNil(item.onMoreInfo)}
								trackingId={generateTrackingId(itemTrackingId, "__more-info")}
							>
								{x}
							</InputWithMoreInfo>
						)}
					>
						<RadioButton
							group={groupName}
							text={item.text}
							value={item.value === value}
							onChange={(x) => changeHandler(x, item.value)}
							isDisabled={isDisabled || item.isDisabled}
							trackingId={itemTrackingId}
							ellipseText={ellipseText}
						/>
					</ConditionalWrapper>
				);
			})}
		</>
	);
};
RadioButtonGroup.displayName = "RadioButtonGroup";
